import React, { useState } from 'react';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { ConditionalRendering } from '../../../components/common/conditional-rendering';
import { INELIGIBILITY_REASONS_MAP } from '../../../constants/individualPortal/eligibility/IneligibilityReasonsMap';
import { IStyledToggleReasonProps } from '../../../constants/individualPortal/eligibility/eligibility.types';

interface IneligibilityReasonsProps {
    ineligibilityReasons: string[];
}

const StyledIneligibilityTitle = styled.div`
    color: ${({ theme }) => theme.gray2};
    font-weight: 600;
`;

const StyledIneligibilityReasonsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const StyledIneligibilityReasons = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    color: ${({ theme }) => theme.gray3};
`;

const StyledIneligibilityProgram = styled.div`
    display: flex;
    justify-content: space-between;
`;

const StyledToggleReason = styled.div<IStyledToggleReasonProps>`
    display: flex;
    flex-direction: row;
    align-items: center;
    cursor: pointer;
    color: ${({ theme }) => theme.primaryDark};
`;

const StyledReasonList = styled.ul`
    margin-left: 16px;
`;

const StyledIcon = styled(FontAwesomeIcon)`
    margin: 4px 0 0 4px;
`;

const UNKNOWN_REASON = 'Unknown reason code';
const UNKNOWN_PROGRAM = 'Unknown Program';

export const IneligibilityReasons = (props: IneligibilityReasonsProps) => {
    const { ineligibilityReasons } = props;
    const [showIneligibilityReasons, setShowIneligibilityReasons] = useState({});

    // Group reasons by program
    const groupedReasons = ineligibilityReasons.reduce((acc, code) => {
        const { program = UNKNOWN_PROGRAM, reason = `${UNKNOWN_REASON} ${code}` } =
            INELIGIBILITY_REASONS_MAP[code] || {};

        acc[program] = acc[program] || [];
        acc[program].push(reason);

        return acc;
    }, {});

    // Handle toggle show/hide
    const toggleProgram = (program: string) => {
        setShowIneligibilityReasons(prev => ({
            ...prev,
            [program]: !prev[program],
        }));
    };
    return (
        <StyledIneligibilityReasonsContainer>
            <StyledIneligibilityTitle>
                <FormattedMessage id="eligibility_results.ineligibility.header" />
            </StyledIneligibilityTitle>
            <StyledIneligibilityReasons>
                {Object.keys(groupedReasons).map(program => {
                    return (
                        <div key={program}>
                            <StyledIneligibilityProgram>
                                <FormattedMessage id={program} />
                                <StyledToggleReason onClick={() => toggleProgram(program)}>
                                    <ConditionalRendering isTrue={!showIneligibilityReasons[program]}>
                                        <FormattedMessage id="eligibility_results.ineligibility.show_reasons" />
                                        <StyledIcon icon="chevron-down" />
                                    </ConditionalRendering>
                                    <ConditionalRendering isTrue={showIneligibilityReasons[program]}>
                                        <FormattedMessage id="eligibility_results.ineligibility.hide_reasons" />
                                        <StyledIcon icon="chevron-up" />
                                    </ConditionalRendering>
                                </StyledToggleReason>
                            </StyledIneligibilityProgram>
                            <ConditionalRendering isTrue={showIneligibilityReasons[program]}>
                                <StyledReasonList>
                                    {groupedReasons[program].map((reason: string) => {
                                        return (
                                            <li key={reason}>
                                                <FormattedMessage id={reason} />
                                            </li>
                                        );
                                    })}
                                </StyledReasonList>
                            </ConditionalRendering>
                        </div>
                    );
                })}
            </StyledIneligibilityReasons>
        </StyledIneligibilityReasonsContainer>
    );
};
